// SparesHub / CarPartsAuction.in legal documents + lightweight renderer parser.
// Raw text is kept verbatim; parseLegal() turns it into renderable blocks.

export function parseLegal(raw) {
  const lines = raw.split("\n").map((l) => l.replace(/\*\*/g, "").replace(/\s+$/, "").trim());
  const blocks = [];
  let bullets = null;
  const flush = () => {
    if (bullets && bullets.length) blocks.push({ type: "ul", items: bullets });
    bullets = null;
  };
  for (const line of lines) {
    if (!line) { flush(); continue; }
    const explicit = /^[*•]\s+/.test(line);
    const implicit = /;$/.test(line) && !/^\d+\.\s/.test(line);
    if (explicit || implicit) {
      if (!bullets) bullets = [];
      bullets.push(line.replace(/^[*•]\s+/, "").trim());
      continue;
    }
    flush();
    if (/^Effective Date:/i.test(line)) { blocks.push({ type: "eff", text: line }); continue; }
    if (/^\d+\.\s+\S/.test(line)) { blocks.push({ type: "h", text: line }); continue; }
    if (/^[A-Z]\.\s+\S/.test(line)) { blocks.push({ type: "sub", text: line }); continue; }
    blocks.push({ type: "p", text: line });
  }
  flush();
  return blocks;
}

const TERMS_OF_USE = `Effective Date: 26 August 2026

These Terms of Use ("Terms") govern your access to and use of CarPartsAuction.in ("Platform", "Website", "Portal"), operated by Iradium Automobiles Private Limited ("Company", "we", "us", "our"). By accessing, registering on, uploading inventory to, submitting an Expression of Interest, placing a bid or enquiry, or otherwise using the Platform, you agree to these Terms. If you do not agree, do not use the Platform.

1. About the Platform
CarPartsAuction.in is a B2B platform intended to facilitate discovery, listing, evaluation, bidding, negotiation and potential sale of automobile spare-parts inventory, including slow-moving stock, dead stock, surplus stock, discontinued stock, dealership closure stock, distributor stock and similar inventory. The Platform may allow sellers to upload inventory files, create lots and receive Expressions of Interest, bids or purchase proposals from interested buyers. Unless specifically stated otherwise for a particular transaction, CarPartsAuction.in acts as a facilitation platform and does not guarantee that any listing, Expression of Interest, bid, negotiation or transaction will result in a completed sale.

2. Eligibility
The Platform is primarily intended for business users including:
* automobile dealerships;
* parts distributors;
* retailers;
* workshops and garages;
* automobile spare-parts traders;
* exporters and importers;
* fleet operators;
* dismantlers or other lawful automotive businesses; and
* other businesses approved by us.
By using the Platform, you represent that you have the authority to act on behalf of the business or entity you represent. We may refuse, restrict, suspend or terminate access to any user at our discretion.

3. Registration and Account Information
Users may be required to provide information including name, company name, address, mobile number, email address, GST details and other business information. You agree that all information provided by you will be accurate, complete and current. You are responsible for:
* maintaining the confidentiality of your account credentials;
* all activity conducted through your account; and
* immediately notifying us of suspected unauthorized access.
We are not liable for loss caused by misuse of your credentials where such misuse is not attributable to us.

4. Seller Listings
Sellers may create inventory lots and upload Excel files, photographs, descriptions and supporting information. By listing inventory, the seller represents and warrants that:
* the seller lawfully owns or has authority to sell the goods;
* the inventory is not stolen, counterfeit or unlawfully obtained;
* the information provided is materially accurate;
* quantities stated are reasonably accurate;
* brand names and part numbers correspond to the goods being offered;
* any material damage, shortage, deterioration or packaging issue known to the seller has been disclosed;
* the sale of the inventory does not violate any contractual or legal restriction applicable to the seller.
Sellers remain responsible for the accuracy and completeness of their listings.

5. Inventory Uploads
The Platform may permit sellers to upload stock lists in Excel, CSV or other supported formats. We may process, normalize, categorize, restructure or display uploaded inventory data for the purpose of presenting the lot to buyers. The Company does not guarantee that:
* every uploaded row will be parsed correctly;
* product descriptions will be complete;
* vehicle compatibility will be accurate;
* MRP or historical pricing will be current;
* part-number data provided by the seller is error-free.
Sellers should review their listings before or after publication and promptly report material errors.

6. Lot Information
A lot may include information such as:
* part numbers;
* descriptions;
* quantities;
* vehicle brands or models;
* MRP;
* condition;
* packaging condition;
* stock age;
* warehouse location;
* photographs; and
* other seller-provided information.
All such information should be treated as indicative unless expressly verified. Buyers are expected to perform their own commercial and physical due diligence.

7. Inspection of Goods
Where inspection is permitted, buyers may inspect the goods at the seller's warehouse or another agreed location. Inspection arrangements may be coordinated through the Platform or offline. The buyer is responsible for evaluating, among other things:
* physical condition;
* quantity;
* packaging;
* corrosion or deterioration;
* authenticity;
* compatibility;
* obsolescence;
* marketability; and
* commercial suitability.
Failure by a buyer to inspect goods does not make the Company responsible for defects that could reasonably have been identified through inspection.

8. Expressions of Interest
The Platform may permit buyers to submit an Expression of Interest ("EOI"). An EOI may include:
* interested quantity;
* indicative offer value;
* preferred inspection requirement;
* proposed payment timeline;
* pickup timeline;
* comments or commercial conditions.
An EOI is generally an indication of commercial interest and does not automatically create a binding purchase contract. Further negotiation may take place offline.

9. Bids and Offers
Where bidding functionality is enabled, buyers may submit bids or offers for a lot. Unless explicitly stated otherwise:
* a bid does not guarantee allocation of the lot;
* the highest bid does not automatically become the successful buyer;
* the seller may accept, reject or negotiate any bid;
* the seller may withdraw a lot before final confirmation;
* the Company may cancel or suspend a bidding process where necessary.
Commercial negotiations may be conducted offline between the relevant parties or through the Company.

10. No Published Seller Reserve Price
The Platform may choose not to display any seller reserve price, minimum acceptable price or internal valuation. The absence of a displayed reserve price does not mean that the seller is obligated to accept any particular offer. Acceptance remains subject to seller confirmation and commercial negotiation.

11. Formation of a Transaction
A transaction becomes binding only when the applicable commercial terms have been expressly confirmed by the relevant parties through a purchase confirmation, purchase order, invoice, written acceptance or other documented confirmation recognized by the Company. Listings, EOIs and bids alone should not be treated as proof that a transaction has been concluded.

12. Escrow
Certain transactions may use an Escrow mechanism or escrow-style payment arrangement to increase transaction security and trust. Where Escrow is offered, the applicable transaction may require the buyer to deposit funds with an approved Escrow service provider before release of goods. The release of Escrow funds may be subject to defined conditions such as:
* seller confirmation;
* inventory verification;
* dispatch;
* pickup;
* delivery;
* buyer acknowledgement; or
* other transaction-specific milestones.
The exact Escrow process, charges and release conditions may vary by transaction. Where a third-party Escrow provider is used, the provider's own terms may also apply.

13. Payment
Unless otherwise agreed, buyers are responsible for making payments within the agreed timeline. Payment instructions may be communicated by the Company, seller or authorized Escrow service provider. Users must verify payment instructions before transferring funds. The Company will not be responsible for payments made to unauthorized bank accounts or persons outside verified transaction instructions.

14. Taxes
Unless stated otherwise, quoted or bid amounts may be exclusive of applicable taxes. GST and other taxes will be applied as legally applicable and as stated in the relevant invoice or transaction documents. Each party remains responsible for its own tax obligations.

15. Logistics and Pickup
Unless specifically agreed otherwise, the responsibility for pickup, freight, transport, loading, unloading and insurance will be determined separately for each transaction. Buyers should not assume that transportation is included merely because goods are listed on the Platform. Once goods are handed over to the agreed transporter or buyer, risk may transfer according to the commercial terms agreed for that transaction.

16. Condition of Goods
The Platform primarily deals with surplus, slow-moving, aged or liquidation inventory. Such inventory may include:
* old stock;
* discontinued part numbers;
* obsolete packaging;
* faded or damaged cartons;
* shelf-worn products;
* parts with older manufacturing dates;
* superseded parts;
* incomplete packaging;
* mixed-condition stock.
Buyers acknowledge the nature of liquidation inventory and should inspect and evaluate goods accordingly.

17. Authenticity and Compatibility
The Company may display OEM names, automobile brands, part numbers, models and other information for identification purposes. Unless expressly verified in writing, the Company does not independently warrant:
* authenticity;
* manufacturer authorization;
* fitment;
* compatibility;
* supersession;
* current MRP; or
* applicability to a particular vehicle.
Buyers should independently verify part numbers and compatibility before purchase.

18. No Warranty by Platform
Except where expressly provided in transaction-specific documents, goods listed on the Platform are offered on an "as available" and "as described" basis. CarPartsAuction.in does not independently provide any manufacturer warranty. Any warranty, return, replacement or claim rights must be specifically agreed as part of the transaction.

19. Returns and Cancellations
Liquidation and bulk inventory transactions are generally commercial transactions negotiated based on quantity, condition and price. Accordingly, once a transaction has been confirmed, cancellations, returns or refunds may not be permitted unless:
* specifically agreed in writing;
* goods materially differ from the agreed description;
* quantities materially differ from agreed quantities; or
* otherwise required by applicable law.
Transaction-specific terms will prevail.

20. Buyer Responsibilities
Buyers must:
* conduct appropriate due diligence;
* inspect goods where necessary;
* verify part numbers;
* assess resale potential;
* verify taxes and logistics;
* submit genuine bids and EOIs;
* honor confirmed commercial commitments; and
* avoid misleading or fraudulent conduct.
The Company may restrict buyers who repeatedly submit non-serious bids or fail to honor confirmed commitments.

21. Seller Responsibilities
Sellers must:
* provide reasonably accurate inventory information;
* make goods available for agreed inspection where applicable;
* disclose known material defects;
* maintain agreed quantities pending confirmed transaction where required;
* provide lawful tax invoices where applicable;
* hand over goods after satisfying applicable payment conditions; and
* cooperate reasonably in resolving verified discrepancies.

22. Seller and Buyer Verification
The Platform may introduce or modify buyer and seller verification mechanisms from time to time. Verification by the Company should not be interpreted as a guarantee of:
* financial capability;
* creditworthiness;
* legal standing;
* authenticity of all representations;
* transaction performance.
Users remain responsible for their own due diligence.

23. Intellectual Property
All Platform software, website design, branding, databases, processes, graphics, documentation and proprietary functionality belong to or are licensed to Iradium Automobiles Private Limited. Users may not:
* copy or reproduce substantial portions of the Platform;
* scrape the Platform at scale;
* reverse engineer Platform functionality;
* use automated systems without permission;
* reproduce proprietary listing data for competing commercial services; or
* misuse the CarPartsAuction.in or SparesHub brands.

24. Seller Content Licence
By uploading inventory data, images or documents, the seller grants the Company a non-exclusive licence to:
* host;
* reproduce;
* format;
* categorize;
* display;
* distribute;
* communicate; and
* process such content for purposes connected with operating, marketing and facilitating the relevant listing or transaction.
The seller retains ownership of its original content.

25. Automobile Brand Names
All vehicle manufacturer names, trademarks, logos and part numbers referenced on the Platform belong to their respective owners. Their use is generally for identification, compatibility reference and informational purposes. Unless expressly stated, CarPartsAuction.in is not endorsed by or affiliated with any automobile manufacturer.

26. Prohibited Conduct
Users must not:
* list stolen or counterfeit goods;
* submit false inventory;
* manipulate bids;
* impersonate another entity;
* misuse buyer or seller information;
* interfere with Platform operation;
* upload malware;
* scrape or systematically extract Platform data without permission;
* attempt unauthorized access;
* use the Platform for unlawful activity.
We may suspend accounts and remove listings without prior notice where reasonably necessary.

27. Platform Availability
The Platform may be modified, suspended or unavailable from time to time. We do not guarantee:
* uninterrupted availability;
* error-free operation;
* permanent retention of every listing;
* continuous availability of every feature; or
* that every buyer or seller will receive commercial interest.

28. No Guarantee of Sale
Listing inventory on CarPartsAuction.in does not guarantee:
* receipt of an EOI;
* receipt of bids;
* achievement of any particular price;
* sale of the entire lot;
* sale within any specific period.
Any indicative valuation or market feedback should not be treated as a guaranteed realization value.

29. Limitation of Liability
To the maximum extent permitted by law, Iradium Automobiles Private Limited shall not be liable for indirect, incidental, consequential, special or loss-of-profit damages arising from:
* unsuccessful listings;
* unsuccessful negotiations;
* buyer or seller default;
* inventory discrepancies;
* transport damage;
* counterfeit goods supplied by a seller;
* inaccurate listing information;
* compatibility errors;
* payment disputes;
* third-party Escrow services;
* third-party logistics services; or
* unauthorized conduct by another user.
Where liability cannot legally be excluded, it shall be limited to the extent permitted under applicable law.

30. Indemnity
You agree to indemnify and hold harmless Iradium Automobiles Private Limited, its directors, employees and representatives against claims, losses, liabilities, costs or expenses arising from:
* your breach of these Terms;
* false or misleading information provided by you;
* unlawful goods listed or purchased by you;
* infringement of third-party rights;
* tax or regulatory violations attributable to you;
* disputes arising from your commercial commitments.

31. Suspension and Termination
We may suspend or terminate an account, remove a listing or restrict Platform access where we believe a user:
* has breached these Terms;
* has engaged in fraud or attempted fraud;
* has repeatedly defaulted on confirmed transactions;
* has uploaded prohibited goods;
* poses a security risk; or
* has otherwise misused the Platform.

32. Changes to the Platform
Features, transaction models, charges, Escrow arrangements and Platform rules may evolve. We may update these Terms periodically. Continued use after updated Terms become effective constitutes acceptance of the revised Terms.

33. Privacy
Use of personal information is governed by the applicable Privacy Policy of CarPartsAuction.in. Users should review the Privacy Policy before using the Platform.

34. Electronic Communications
You consent to receiving transactional communications relating to:
* account activity;
* listings;
* EOIs;
* bids;
* inspections;
* negotiations;
* payments;
* Escrow;
* transactions;
* support.
Communications may be sent through email, telephone, SMS, WhatsApp or Platform notifications where permitted.

35. Governing Law
These Terms shall be governed by the laws of India. Subject to any applicable arbitration arrangement, courts at Pune, Maharashtra shall have exclusive jurisdiction over disputes relating to the Platform.

36. Severability
If any provision of these Terms is held invalid or unenforceable, the remaining provisions will continue in effect.

37. Contact Us
For questions regarding these Terms or CarPartsAuction.in:
Iradium Automobiles Private Limited
Pune, Maharashtra, India
Contact Person: Tapas Gupta
Email: contact@spareshub.com
Phone: +91 7875523381
Website: CarPartsAuction.in`;

const PRIVACY_POLICY = `Effective Date: 26 August 2026

This Privacy Policy explains how CarPartsAuction.in ("Platform", "Website", "Portal") collects, uses, stores and protects information when you access or use the Platform. CarPartsAuction.in is operated by Iradium Automobiles Private Limited ("Company", "we", "us", "our"), Pune, Maharashtra, India. By accessing, registering on or using the Platform, you acknowledge the practices described in this Privacy Policy.

1. Scope of this Policy
This Privacy Policy applies to information collected through:
* CarPartsAuction.in;
* user registration and login;
* seller lot creation;
* inventory Excel/CSV uploads;
* Expressions of Interest and bids;
* inspection requests;
* buyer and seller communications;
* Escrow-related transaction coordination;
* customer support;
* emails, telephone calls, SMS and WhatsApp communications relating to the Platform.
The Platform is primarily intended for business-to-business users.

2. Information We Collect
We may collect the following categories of information.

A. Account and Contact Information
When you register or interact with the Platform, we may collect:
* name;
* mobile number;
* email address;
* company or business name;
* business address;
* city, state and PIN code;
* GSTIN or other business identification details where required;
* designation or role;
* account login information.

B. Seller Information
Sellers may provide information including:
* company details;
* warehouse or stock location;
* contact-person details;
* inventory descriptions;
* part numbers;
* vehicle brands and models;
* quantities;
* MRP or pricing information;
* stock age;
* product condition;
* packaging condition;
* photographs;
* invoices or supporting documents;
* remarks;
* inspection availability.

C. Inventory Files
Sellers may upload Excel, CSV or other supported files containing spare-parts inventory. These files may contain:
* part numbers;
* descriptions;
* brands;
* models;
* categories;
* quantities;
* prices;
* locations;
* stock-age information;
* remarks and other inventory-related data.
We may process, normalize, structure, categorize and display information contained in these files for purposes of operating the Platform.

D. Buyer Information
Buyers may provide information including:
* business details;
* contact information;
* Expressions of Interest;
* bid or offer amounts;
* desired quantities;
* inspection requests;
* commercial comments;
* pickup preferences;
* transaction-related communications.

E. Transaction Information
When a transaction progresses, we may collect information such as:
* buyer and seller details;
* agreed commercial terms;
* invoice information;
* payment status;
* Escrow status;
* pickup and dispatch information;
* logistics information;
* transaction notes;
* dispute or discrepancy information.

F. Escrow Information
Where transactions use Escrow, we may collect or receive information concerning:
* payment status;
* amount deposited;
* Escrow reference number;
* release status;
* transaction milestones;
* buyer and seller confirmation.
Actual payment processing may be performed by a third-party Escrow or payment service provider. We do not intend to store complete credit-card, debit-card or banking credentials unless specifically required and lawfully permitted.

G. Inspection Information
Where buyers request stock inspection, we may collect:
* preferred inspection date;
* inspection location;
* visitor names;
* contact details;
* inspection notes;
* photographs;
* comments or observations.

H. Communications
We may retain communications made through or relating to the Platform, including:
* support requests;
* email correspondence;
* telephone-call records or notes;
* SMS;
* WhatsApp communications;
* transaction discussions;
* complaints;
* feedback.

I. Technical and Usage Information
When you use the Platform, we may automatically collect information such as:
* IP address;
* browser type;
* device type;
* operating system;
* pages visited;
* login activity;
* timestamps;
* referral information;
* session information;
* error and diagnostic information;
* interaction with Platform features.

3. How We Use Information
We may use information for purposes including:
* creating and managing user accounts;
* publishing and managing seller inventory;
* processing inventory files;
* displaying lots to potential buyers;
* enabling buyers to submit EOIs and bids;
* facilitating buyer-seller communication;
* arranging stock inspections;
* supporting negotiations;
* coordinating Escrow transactions;
* facilitating payments, pickups and logistics;
* providing customer support;
* detecting fraud or misuse;
* improving Platform functionality;
* analysing Platform usage;
* maintaining security;
* enforcing our Terms of Use;
* fulfilling legal or regulatory obligations;
* resolving disputes;
* communicating transaction and service updates.

4. Use of Inventory Data
Inventory information uploaded by sellers may be:
* processed automatically;
* categorized;
* reformatted;
* summarized;
* displayed to buyers;
* used to create lot summaries;
* used to calculate statistics such as total quantity, estimated MRP value, brand mix or part-number count.
We may also use aggregated or de-identified inventory information to understand market activity and improve the Platform. We will not knowingly represent confidential seller information as public where it has been specifically designated as confidential and is not necessary for displaying the relevant lot.

5. Publicly Displayed Information
Certain information submitted by sellers may be visible to registered or prospective buyers, including:
* lot title;
* inventory details;
* brands;
* part numbers;
* descriptions;
* quantities;
* location;
* condition;
* photographs;
* approximate stock value;
* other listing information.
Users should not upload personal, confidential or sensitive information into fields intended for public listing.

6. Buyer and Seller Identity
CarPartsAuction.in may limit, delay or control disclosure of buyer or seller identity depending on the transaction process. Where necessary for:
* inspection;
* negotiation;
* transaction completion;
* Escrow;
* invoicing;
* logistics;
* legal compliance,
relevant buyer and seller information may be shared with the applicable parties.

7. Sharing of Information
We may share information with the following categories of recipients.

A. Buyers and Sellers
Information may be shared where necessary to facilitate:
* enquiries;
* EOIs;
* inspections;
* negotiations;
* confirmed transactions;
* invoicing;
* pickup or delivery;
* dispute resolution.

B. Escrow and Payment Providers
Where Escrow or payment services are used, transaction information may be shared with the relevant service provider. Their independent privacy policies and terms may apply.

C. Logistics Providers
Information such as:
* names;
* addresses;
* telephone numbers;
* warehouse locations;
* shipment information
may be shared with transporters, couriers or logistics providers.

D. Technology and Service Providers
We may engage providers for:
* website hosting;
* cloud infrastructure;
* analytics;
* email;
* SMS;
* WhatsApp or messaging;
* data storage;
* cybersecurity;
* customer support;
* software development;
* document processing.
These providers may process information only to the extent required to provide their services.

E. Professional Advisers
We may disclose information to:
* lawyers;
* accountants;
* auditors;
* consultants;
* insurers
where reasonably necessary.

F. Government and Legal Authorities
We may disclose information where required by:
* applicable law;
* court order;
* government authority;
* law-enforcement request;
* tax authority;
* regulatory requirement.

8. We Do Not Sell Personal Data
We do not sell personal information to third parties as a standalone commercial product. We may use aggregated, statistical or de-identified information for legitimate business analysis where individuals cannot reasonably be identified.

9. Cookies and Similar Technologies
CarPartsAuction.in may use cookies and similar technologies for:
* login sessions;
* authentication;
* security;
* user preferences;
* performance;
* analytics;
* Platform functionality.
Some cookies may be essential to the operation of the Platform. Users may control cookies through their browser settings, although disabling essential cookies may affect Platform functionality.

10. Analytics
We may use analytics tools to understand:
* traffic;
* user journeys;
* feature usage;
* errors;
* device types;
* performance;
* general Platform engagement.
Analytics information may be collected in aggregated or pseudonymous form depending on the service used.

11. Data Security
We use reasonable administrative, technical and organizational safeguards intended to protect information against:
* unauthorized access;
* alteration;
* misuse;
* loss;
* destruction;
* unauthorized disclosure.
Such measures may include:
* access controls;
* password protection;
* encrypted transmission;
* restricted administrative access;
* secure hosting;
* backup processes;
* audit logs;
* monitoring.
However, no electronic system is completely secure, and we cannot guarantee absolute security.

12. Account Security
Users are responsible for:
* protecting passwords;
* restricting access to their devices;
* maintaining accurate account information;
* notifying us of suspected unauthorized use.
Users should not share login credentials with unauthorized persons.

13. Data Retention
We may retain information for as long as reasonably necessary for:
* account operation;
* ongoing transactions;
* legal obligations;
* accounting;
* taxation;
* dispute resolution;
* fraud prevention;
* enforcement of contractual rights;
* legitimate business records.
Different types of information may be retained for different periods. Transaction, invoice, payment, Escrow and compliance records may need to be retained even after account closure.

14. Account Closure and Data Deletion
Users may request closure of their account or deletion of eligible personal information by contacting contact@spareshub.com. We may request verification before processing a deletion request. Certain information may not be deleted immediately where retention is required for:
* legal compliance;
* tax records;
* completed or disputed transactions;
* fraud prevention;
* accounting requirements;
* contractual claims.
Where deletion is not possible, information may be restricted, archived or anonymized where appropriate.

15. Seller Inventory After Account Closure
Where a seller closes its account:
* active listings may be removed or deactivated;
* uploaded inventory files may be deleted or archived;
* completed transaction records may be retained;
* aggregated or anonymized data may continue to be used.

16. User Rights
Subject to applicable law, users may request:
* access to personal information;
* correction of inaccurate information;
* updating of information;
* deletion of eligible information;
* closure of account;
* withdrawal of consent where processing is based on consent.
Requests may be sent to the contact details below.

17. Business Information
Certain information provided through the Platform may relate to businesses rather than individuals. Business information such as:
* inventory;
* company name;
* GSTIN;
* warehouse location;
* commercial offers;
* pricing;
* bids
may not constitute personal data in every circumstance, but we nevertheless handle such information with reasonable care.

18. Confidential Commercial Information
Users should avoid uploading information unrelated to the transaction that is confidential or commercially sensitive. Where confidential information is necessary, users should clearly identify it. The Platform is not intended to serve as a secure virtual data room.

19. Third-Party Links
The Platform may contain links to third-party websites or services. We are not responsible for the privacy practices, content or security of third-party services. Users should review the applicable third-party privacy policies independently.

20. Third-Party Escrow and Payment Services
Escrow or payment services may be provided by independent third parties. Such providers may independently collect:
* KYC details;
* bank information;
* transaction information;
* identification documents.
Information collected directly by those providers will be governed by their own privacy policies.

21. Communications
We may communicate with users through:
* email;
* telephone;
* SMS;
* WhatsApp;
* Platform notifications.
Transactional communications may include:
* listing updates;
* EOI notifications;
* inspection coordination;
* bid updates;
* payment information;
* Escrow updates;
* logistics updates;
* support messages.
Marketing communications may be sent where permitted. Users may opt out of non-essential marketing communications.

22. Fraud Prevention and Platform Security
We may process information to detect and prevent:
* fraudulent accounts;
* fake inventory;
* stolen goods;
* counterfeit listings;
* payment fraud;
* suspicious bidding;
* unauthorized Platform access;
* abuse of the Portal.
This may include review of account activity, IP addresses, transaction behaviour and user communications.

23. Children
CarPartsAuction.in is a B2B Platform intended for adults acting in a commercial or professional capacity. The Platform is not intended for children. We do not knowingly solicit personal information from persons under the age required to legally enter into commercial transactions.

24. International Users
The Platform is operated from India. If a user accesses the Platform from outside India, information may be processed and stored in India or other locations used by our service providers. By using the Platform, you acknowledge that data-protection rules may differ between jurisdictions.

25. Cross-Border Data Processing
Certain cloud, analytics, communication or technology providers may process information outside India. Where applicable, we seek to use service providers with appropriate security and contractual safeguards.

26. Changes to This Privacy Policy
We may update this Privacy Policy from time to time to reflect:
* changes to Platform functionality;
* new services;
* changes to applicable law;
* changes to data-processing practices.
The revised policy will be posted on CarPartsAuction.in with an updated effective date. Continued use of the Platform after the updated policy becomes effective constitutes acknowledgement of the revised policy.

27. Grievance and Privacy Contact
For privacy questions, complaints, correction requests or data-deletion requests, contact:
Iradium Automobiles Private Limited
Pune, Maharashtra, India
Contact Person: Tapas Gupta
Email: contact@spareshub.com
Phone: +91 7875523381
Website: CarPartsAuction.in
We will make reasonable efforts to respond to legitimate privacy requests within a reasonable period.

28. Governing Law
This Privacy Policy shall be governed by applicable laws of India. Subject to applicable law, courts at Pune, Maharashtra shall have jurisdiction over disputes arising in connection with this Privacy Policy.`;

const LEGAL_DISCLAIMER = `Effective Date: 26 August 2026

CarPartsAuction.in is operated by Iradium Automobiles Private Limited, Pune, Maharashtra, India. This Legal Disclaimer applies to all users of CarPartsAuction.in, including sellers, buyers, bidders, visitors and other persons accessing or using the Platform. By using the Platform, you acknowledge and agree to the following.

1. Platform Facilitation Only
CarPartsAuction.in is a B2B platform that facilitates the listing, discovery, inspection, bidding, negotiation and potential sale of automobile spare-parts inventory. Unless expressly stated otherwise in a specific transaction, the Platform acts only as a facilitator and does not itself manufacture, own, certify, inspect or guarantee the goods listed by third-party sellers. The Platform does not guarantee that any listing, bid, Expression of Interest, negotiation or transaction will result in a completed sale.

2. Seller-Provided Information
Information relating to lots, inventory, quantities, part numbers, descriptions, brands, vehicle models, MRP, stock age, condition, packaging, location and photographs may be provided by sellers. CarPartsAuction.in does not independently guarantee that such information is complete, current or error-free. Sellers remain responsible for the accuracy of their listings.

3. Buyer Due Diligence
Buyers are responsible for conducting their own commercial and technical due diligence before purchasing any lot. This includes verification of: physical condition; quantities; packaging; authenticity; part numbers; supersession; vehicle compatibility; manufacturing dates; stock age; marketability; resale potential; applicable taxes; logistics requirements. Where physical inspection is available, buyers are encouraged to inspect the goods before concluding the transaction.

4. Liquidation and Aged Inventory
Users acknowledge that lots offered through the Platform may consist of: slow-moving inventory; dead stock; surplus stock; dealership closure stock; distributor inventory; aged stock; discontinued parts; superseded part numbers; obsolete packaging; shelf-worn stock; damaged or faded packaging; mixed-condition inventory. Such characteristics are inherent in liquidation transactions and should be considered when evaluating a lot.

5. No Guarantee of Authenticity
Unless specifically verified and confirmed in writing, CarPartsAuction.in does not independently certify the authenticity of any spare part. Any representation regarding genuine, OEM, OES, aftermarket or other product classification may be based on information supplied by the seller. Buyers should independently verify authenticity wherever material to the transaction.

6. No Guarantee of Compatibility or Fitment
References to: vehicle makes; vehicle models; variants; part numbers; fitment; compatibility; supersession are provided for informational purposes. CarPartsAuction.in does not guarantee that a listed part will fit or function in any specific vehicle. Users should verify compatibility using appropriate technical catalogues, manufacturer information or qualified professionals.

7. Pricing and MRP Disclaimer
Any MRP, estimated value, historical price, indicative valuation or total stock value shown on the Platform is provided for informational purposes only. Such values should not be interpreted as: guaranteed resale value; current market price; expected realization value; guaranteed profitability; recommended purchase price. Market conditions, part obsolescence, stock age, brand, condition and demand may materially affect actual value.

8. No Investment or Commercial Advice
Information displayed on CarPartsAuction.in does not constitute financial, investment, accounting, tax or commercial advice. Users must make their own business decisions and seek professional advice where appropriate.

9. Expressions of Interest and Bids
An Expression of Interest, bid or offer does not by itself create a binding transaction unless expressly accepted and confirmed in accordance with the applicable transaction terms. The highest bid does not necessarily guarantee allocation. Sellers may accept, reject or negotiate offers unless otherwise specified.

10. Offline Negotiations
Certain commercial negotiations may occur offline. Any direct communication permitted between users remains subject to the applicable Buyer Terms, Seller Terms, confidentiality obligations and non-circumvention provisions. Direct communication does not authorize either party to bypass CarPartsAuction.in or SparesHub.

11. Non-Circumvention
Buyers and sellers introduced through CarPartsAuction.in or SparesHub are subject to applicable non-circumvention obligations. Users must not use information, introductions, inspections or commercial opportunities obtained through the Platform to bypass SparesHub and conclude unauthorized direct transactions. Applicable remedies are governed by the Buyer Terms, Seller Terms and transaction-specific agreements.

12. Escrow Disclaimer
Certain transactions may use Escrow or an escrow-style payment arrangement. Where a third-party Escrow provider is used: the provider's independent terms may apply; CarPartsAuction.in does not control the provider's systems; release of funds may depend on transaction-specific milestones; processing times may depend on the service provider. The presence of Escrow does not constitute a guarantee by CarPartsAuction.in of product quality, authenticity or transaction performance.

13. Payments
Users should verify all payment instructions before transferring funds. CarPartsAuction.in shall not be responsible for payments made: to unauthorized persons; to incorrect bank accounts; outside approved payment instructions; pursuant to fraudulent communications not originating from authorized Platform channels. Users should report suspicious payment requests immediately.

14. Logistics and Transport
Unless specifically agreed otherwise, CarPartsAuction.in does not guarantee: pickup timelines; transporter performance; delivery timelines; freight charges; transit condition; insurance coverage. Responsibility for logistics, freight, loading, unloading, insurance and risk transfer will depend on the terms of the particular transaction.

15. Inspection Disclaimer
Where CarPartsAuction.in facilitates an inspection, such facilitation does not mean that the Company has inspected, approved or certified the goods. Any inspection performed by a buyer, agent or third party is for the buyer's own evaluation.

16. No Warranty by Platform
Except where expressly agreed in writing, CarPartsAuction.in does not provide any warranty regarding goods listed on the Platform. Any manufacturer warranty, seller warranty, replacement right, return right or claim must be separately confirmed as part of the relevant transaction.

17. Returns, Refunds and Cancellations
Bulk and liquidation inventory transactions may be subject to restricted or no-return terms. Users should review the specific transaction terms before confirming a purchase. CarPartsAuction.in does not guarantee cancellation, return or refund rights unless specifically provided under the applicable transaction terms or required by law.

18. Third-Party Sellers and Buyers
CarPartsAuction.in does not guarantee the: solvency; creditworthiness; financial capability; performance; conduct; legal standing of any buyer or seller. Any verification performed by the Platform is intended to assist the transaction process and does not constitute an absolute guarantee.

19. Third-Party Services
The Platform may rely on third parties for services including: Escrow; payments; logistics; communications; analytics; hosting; cloud infrastructure. CarPartsAuction.in is not responsible for interruptions, errors or failures caused by independent third-party service providers beyond its reasonable control.

20. Brand Names and Trademarks
Automobile manufacturer names, logos, trademarks, vehicle names and part numbers displayed on the Platform belong to their respective owners. Their use is for identification, reference and informational purposes. Unless expressly stated otherwise, CarPartsAuction.in, SparesHub and Iradium Automobiles Private Limited are not affiliated with, authorized by or endorsed by the automobile manufacturers whose names or trademarks may appear on the Platform.

21. Intellectual Property
All proprietary rights in CarPartsAuction.in, including its: software; design; branding; databases; processes; documentation; content created by the Company belong to or are licensed to Iradium Automobiles Private Limited. Unauthorized copying, scraping, reproduction, reverse engineering or commercial reuse is prohibited.

22. Availability of the Platform
CarPartsAuction.in is provided on an "as available" basis. The Company does not guarantee uninterrupted or error-free availability. The Platform may be: updated; modified; temporarily unavailable; suspended; discontinued without liability for ordinary interruptions.

23. Errors and Omissions
Although reasonable efforts may be made to maintain accurate information, the Platform may contain: typographical errors; technical errors; outdated information; incorrectly processed inventory data; incomplete descriptions. Users should independently verify material information before relying on it.

24. No Guarantee of Sale
CarPartsAuction.in does not guarantee that a seller will: receive bids; receive an EOI; achieve a particular price; sell the entire lot; sell within a particular timeframe. Likewise, buyers are not guaranteed access to or allocation of any particular lot.

25. Limitation of Liability
To the maximum extent permitted under applicable law, Iradium Automobiles Private Limited shall not be liable for indirect, incidental, special, consequential or loss-of-profit damages arising from: inaccurate listings; unsuccessful transactions; buyer or seller default; counterfeit or defective goods supplied by a seller; quantity discrepancies; compatibility issues; pricing errors; transport damage; payment disputes; Escrow delays; third-party service failures; unauthorized direct dealings between users. Nothing in this disclaimer excludes liability that cannot legally be excluded.

26. User Responsibility
Users are responsible for ensuring compliance with: applicable laws; tax requirements; GST obligations; invoicing requirements; import/export rules where relevant; resale regulations; intellectual-property laws; contractual restrictions applicable to them.

27. Changes to this Disclaimer
CarPartsAuction.in may update this Legal Disclaimer from time to time. The latest version published on the Website shall apply from its stated effective date.

28. Governing Law and Jurisdiction
This Legal Disclaimer shall be governed by the laws of India. Subject to applicable law and any agreed arbitration arrangement, courts at Pune, Maharashtra shall have exclusive jurisdiction.

29. Contact
For questions regarding this Legal Disclaimer:
Iradium Automobiles Private Limited
Pune, Maharashtra, India
Contact Person: Tapas Gupta
Email: contact@spareshub.com
Phone: +91 7875523381
Website: CarPartsAuction.in`;

const BUYER_TERMS = `Effective Date: 26 August 2026

These Buyer Terms and Conditions ("Buyer Terms") govern the registration of buyers, submission of Expressions of Interest, bids, inspections, negotiations and purchase transactions facilitated through CarPartsAuction.in ("Platform"), operated by Iradium Automobiles Private Limited, Pune, Maharashtra, India ("Company", "SparesHub", "we", "us", "our").

By registering as a buyer, submitting an EOI, placing a bid, requesting an inspection, communicating with a seller introduced through the Platform, or otherwise using the Platform, the Buyer agrees to be legally bound by these Buyer Terms.

These Buyer Terms are in addition to the general Terms of Use and Privacy Policy of CarPartsAuction.in. In the event of conflict regarding buyer obligations, these Buyer Terms shall prevail.

1. Buyer Eligibility and Authority
The Buyer represents and warrants that:
* it is legally capable of entering into commercial transactions;
* the person registering or accepting these Buyer Terms has authority to bind the Buyer;
* all business information submitted is accurate;
* the Buyer is acting for genuine commercial purposes;
* the Buyer will not use the Platform for fraudulent, unlawful or speculative purposes.
The Company may restrict, suspend or reject any buyer account at its discretion.

2. Nature of the Platform
CarPartsAuction.in facilitates the discovery, inspection, bidding, negotiation and purchase of automobile spare-parts inventory, including slow-moving, surplus, aged, discontinued and liquidation stock. The Platform may facilitate:
* seller introductions;
* inventory review;
* Expressions of Interest;
* bids and offers;
* physical inspection;
* commercial negotiation;
* Escrow;
* payment coordination;
* logistics;
* transaction closure.
Unless expressly stated otherwise, the Platform does not guarantee that a listed lot will remain available or that any bid or EOI will be accepted.

3. Buyer Account Information
The Buyer may be required to provide:
* name;
* company name;
* address;
* mobile number;
* email address;
* GSTIN;
* business type;
* designation;
* other commercial information.
The Buyer agrees to keep such information current and accurate.

4. Expressions of Interest
The Buyer may submit an Expression of Interest ("EOI") in relation to a lot. An EOI may include:
* desired quantity;
* indicative offer;
* inspection requirement;
* preferred pickup timeline;
* comments;
* commercial conditions.
An EOI does not automatically create a binding contract unless specifically confirmed in writing. The Buyer shall submit EOIs only where there is genuine commercial interest.

5. Bids and Offers
Where bidding is enabled, the Buyer may submit bids or commercial offers. The Buyer acknowledges that:
* the highest bid may not automatically win;
* the seller may accept, reject or counter any offer;
* the Company may facilitate offline negotiation;
* the seller may impose transaction-specific conditions;
* allocation may depend on factors other than price.
The Buyer shall not submit artificial, misleading or non-serious bids.

6. Seller Introductions by SparesHub
A seller shall be considered an "Introduced Seller" where the seller, its stock, representatives, warehouse, contact details or commercial opportunity is made known to the Buyer through:
* CarPartsAuction.in;
* SparesHub;
* an inventory listing;
* inspection arranged by SparesHub;
* telephone, email, WhatsApp or other communication facilitated by SparesHub;
* EOI, bid or negotiation;
* any other introduction resulting from SparesHub's efforts.
An introduction does not require formal disclosure of the seller's identity at the outset.

7. Non-Circumvention Obligation
The Buyer shall not directly or indirectly bypass, circumvent or attempt to bypass SparesHub in relation to any Introduced Seller. Without limiting the above, the Buyer shall not:
* purchase directly from an Introduced Seller outside the Platform;
* negotiate directly with an Introduced Seller for the purpose of excluding SparesHub;
* enter into a side agreement with an Introduced Seller;
* request a direct invoice in order to avoid SparesHub;
* route the transaction through an affiliate, employee, related business, intermediary or nominee;
* obtain goods through another company controlled by or connected to the Buyer;
* purchase substantially the same inventory directly after learning of it through SparesHub;
* divide a transaction into smaller transactions to avoid SparesHub;
* request that the seller conceal direct dealings;
* encourage the seller to bypass SparesHub;
* use information obtained through the Platform to establish an off-platform commercial relationship intended to avoid SparesHub.
This restriction applies regardless of whether the direct approach is initiated by the Buyer or the Seller.

8. Non-Circumvention Period
The non-circumvention obligation applies during the Buyer's relationship with the Platform and for 24 months from the date of the most recent introduction, inspection, communication, bid, EOI or negotiation involving the Introduced Seller, whichever is later. This restriction applies to:
* the original lot;
* part of the lot;
* substantially similar inventory;
* replacement stock;
* future stock offered by the same seller where the commercial relationship arose through SparesHub.

9. Existing Seller Relationships
If the Buyer already had a genuine commercial relationship with an Introduced Seller before the introduction through SparesHub, the Buyer must notify SparesHub within 3 business days and provide reasonable documentary evidence. Failure to notify within this period will result in the seller being treated as an Introduced Seller under these Buyer Terms. A historical enquiry or possession of contact details alone will not necessarily qualify as a pre-existing commercial relationship.

10. Mandatory Disclosure of Direct Contact
If an Introduced Seller contacts the Buyer directly, the Buyer must notify SparesHub within 24 hours. The Buyer shall not conceal:
* direct calls;
* messages;
* meetings;
* quotations;
* purchase orders;
* invoices;
* payment arrangements;
* direct commercial offers.

11. Consequences of Circumvention
If the Buyer directly or indirectly circumvents SparesHub, the Buyer shall be liable to pay the Company 10% of the gross transaction or lot value, calculated on the higher of:
* actual transaction value;
* negotiated value;
* invoice value;
* purchase order value;
* reasonably ascertainable commercial value of the goods transferred, plus applicable taxes, recovery costs and damages suffered by the Company.
The parties acknowledge that such amount is intended as a genuine pre-estimate of commercial loss, platform value, opportunity loss and facilitation value arising from circumvention. Where actual losses exceed such amount, the Company may seek additional damages or other remedies available under law.

12. Concealed or Split Transactions
Where circumvention involves:
* multiple invoices;
* related entities;
* split purchases;
* substitute buyers;
* indirect payment;
* staggered pickup;
* associated companies,
the Company may aggregate the transactions when determining the relevant transaction value.

13. Inspection of Goods
The Buyer is encouraged to inspect liquidation or aged inventory where practical. The Buyer is responsible for evaluating:
* quantity;
* condition;
* packaging;
* corrosion;
* deterioration;
* authenticity;
* part numbers;
* compatibility;
* stock age;
* marketability;
* resale suitability.
Failure to inspect will not shift responsibility to the Company for matters that could reasonably have been discovered during inspection.

14. Inspection Conduct
Where an inspection is arranged, the Buyer agrees to:
* attend at the agreed time;
* follow reasonable warehouse and safety instructions;
* avoid interfering with seller operations;
* not remove goods or documents without permission;
* not use the inspection to circumvent SparesHub.
The Buyer shall not treat physical access to the seller's premises as permission to establish a direct off-platform transaction.

15. Confidentiality of Seller Information
Information relating to an Introduced Seller is confidential and may include:
* seller identity;
* contact details;
* warehouse address;
* inventory data;
* quantities;
* pricing;
* seller expectations;
* transaction terms;
* stock condition;
* photographs;
* documents;
* negotiation information.
The Buyer shall use such information solely for evaluating or completing transactions facilitated through the Platform.

16. Non-Disclosure Agreement
The Buyer shall:
* maintain Confidential Information in confidence;
* use it only for legitimate evaluation or transaction purposes;
* disclose it only to employees or advisers who need access;
* ensure such persons maintain confidentiality;
* not disclose seller information to competing buyers;
* not publish inventory files or seller information externally;
* not use seller information to create a competing sourcing database;
* promptly notify SparesHub of unauthorized disclosure.

17. Confidentiality Exclusions
Confidential Information does not include information that the Buyer can demonstrate:
* was lawfully known before disclosure;
* became public without breach;
* was lawfully obtained from an unrelated third party;
* was independently developed without reference to confidential information.

18. Confidentiality Period
Confidentiality obligations shall continue for 5 years from disclosure, except for trade secrets, commercial databases and proprietary processes, which remain protected while confidential.

19. Buyer Due Diligence
The Buyer is solely responsible for assessing whether a lot is commercially suitable. The Company does not guarantee:
* resale demand;
* profitability;
* fitment;
* OEM authorization;
* current MRP;
* supersession;
* market value;
* future availability.
Any data displayed on the Platform should be independently verified where material to the transaction.

20. Condition of Liquidation Inventory
The Buyer acknowledges that lots may contain:
* aged stock;
* discontinued inventory;
* obsolete packaging;
* shelf wear;
* faded cartons;
* old manufacturing dates;
* superseded parts;
* mixed-condition stock;
* slow-moving inventory.
The Buyer should account for such characteristics while bidding or negotiating.

21. No Automatic Warranty
Unless expressly agreed in writing, the Company does not provide manufacturer warranty on goods listed through the Platform. Any warranty, replacement or return right must form part of the specific commercial transaction.

22. Pricing and Offers
Any MRP, valuation, estimated stock value or commercial indication displayed on the Platform is informational unless specifically confirmed. The Buyer shall not rely solely on displayed MRP or historical pricing when assessing value.

23. Transaction Confirmation
A transaction becomes binding only after commercial terms are expressly confirmed through an accepted offer, purchase order, invoice, written confirmation or other documented transaction mechanism. An EOI or bid alone does not constitute a completed purchase.

24. Payment Obligations
Once a transaction is confirmed, the Buyer shall make payment according to the agreed timeline and structure. The Buyer shall not intentionally delay or withhold payment after final confirmation except in case of a bona fide dispute. Repeated failure to honor confirmed transactions may result in suspension.

25. Escrow
Where a transaction requires Escrow, the Buyer agrees to:
* deposit funds in the prescribed Escrow arrangement;
* comply with verification requirements;
* follow agreed release conditions;
* not make unauthorized direct payments to the seller.
Any request by a seller to bypass Escrow should be reported to SparesHub immediately.

26. Payment Security
The Buyer must verify payment instructions before transferring funds. The Buyer should not transfer funds to:
* personal accounts;
* unverified third-party accounts;
* accounts communicated outside approved channels, unless specifically confirmed by SparesHub.
The Company will not be liable for losses arising from payments made contrary to verified payment instructions.

27. Taxes
The Buyer is responsible for:
* verifying applicable GST treatment;
* providing correct billing information;
* maintaining its own tax records;
* claiming input tax credit only where legally permissible.

28. Pickup and Logistics
Where pickup is the Buyer's responsibility, the Buyer shall:
* arrange transport within the agreed period;
* provide vehicle/transporter details where required;
* coordinate loading;
* bear agreed freight and insurance costs;
* collect inventory without unreasonable delay.
Storage or holding costs may apply where pickup is materially delayed after transaction completion.

29. Risk After Handover
Risk in the goods shall transfer according to the transaction-specific terms. Where the Buyer or its nominated transporter takes possession of goods, subsequent transit damage is generally the responsibility of the Buyer unless agreed otherwise.

30. No Cancellation After Final Acceptance
Once a final offer has been accepted and the transaction confirmed, the Buyer may not cancel merely because:
* resale prospects change;
* another source offers a lower price;
* market demand changes;
* the Buyer changes its commercial preference.
Unauthorized cancellation may result in forfeiture of agreed amounts, recovery of transaction costs, suspension or other remedies permitted by law.

31. Discrepancies
If the Buyer identifies a material discrepancy in:
* quantity;
* condition;
* description;
* inventory supplied,
the Buyer must notify SparesHub promptly and provide supporting evidence. Minor differences inherent in bulk liquidation inventory may not automatically justify cancellation of the entire transaction.

32. Prohibited Buyer Conduct
The Buyer shall not:
* submit fake EOIs;
* manipulate bids;
* collude with other buyers;
* impersonate another business;
* harass sellers;
* misuse seller information;
* scrape Platform data;
* create duplicate accounts to manipulate bidding;
* attempt unauthorized access;
* use the Platform for unlawful purposes.

33. No Data Scraping or Database Building
The Buyer shall not systematically extract:
* seller identities;
* inventory data;
* part numbers;
* pricing;
* contact details;
* stock lists
for creation of an independent database, competitive marketplace, sourcing directory or similar commercial service without written permission.

34. Platform Fees
Buyer-side charges, where applicable, may include:
* buyer premium;
* transaction fee;
* Escrow fee;
* inspection fee;
* logistics fee;
* other agreed service charges.
Applicable charges will be communicated before they become payable.

35. Suspension and Termination
The Company may suspend or terminate a Buyer account where there is reasonable evidence of:
* circumvention;
* fraudulent bidding;
* repeated transaction default;
* misuse of seller information;
* confidentiality breach;
* payment misconduct;
* abuse of the Platform.

36. Indemnity
The Buyer agrees to indemnify Iradium Automobiles Private Limited and its directors, employees and representatives against claims, damages, losses, costs and expenses arising from:
* breach of these Buyer Terms;
* circumvention;
* misuse of seller information;
* breach of confidentiality;
* false representations;
* unlawful use of purchased inventory;
* tax violations attributable to the Buyer;
* payment default;
* third-party claims caused by the Buyer's conduct.

37. Injunctive Relief
The Buyer acknowledges that circumvention, misuse of seller information or breach of confidentiality may cause harm that cannot always be adequately remedied through monetary damages. The Company may therefore seek appropriate injunctive or equitable relief in addition to monetary remedies.

38. Survival
The following obligations survive suspension, termination or closure of the Buyer account:
* non-circumvention;
* confidentiality;
* NDA obligations;
* payment obligations;
* indemnities;
* dispute provisions;
* restrictions on misuse of seller information.
Closing the account does not terminate obligations relating to sellers introduced before closure.

39. Electronic Acceptance
Selecting: "I Agree", "Accept Buyer Terms", or an equivalent electronic acceptance mechanism constitutes binding acceptance of these Buyer Terms. Electronic logs and records maintained by the Platform may be relied upon as evidence of acceptance.

40. Amendments
The Company may amend these Buyer Terms from time to time. Material amendments may be communicated through the Platform or registered contact information. Confirmed transactions may remain subject to the terms applicable when they were confirmed.

41. Governing Law and Jurisdiction
These Buyer Terms are governed by the laws of India. Courts at Pune, Maharashtra shall have exclusive jurisdiction, subject to any arbitration arrangement separately agreed between the parties.

42. Contact
Iradium Automobiles Private Limited
Pune, Maharashtra, India
Contact Person: Tapas Gupta
Email: contact@spareshub.com
Phone: +91 7875523381
Platform: CarPartsAuction.in`;

const SELLER_TERMS = `Effective Date: 26 August 2026

These Seller Terms and Conditions ("Seller Terms") govern the registration of sellers, listing of inventory, participation in lot sales, buyer introductions, negotiations and transactions facilitated through CarPartsAuction.in ("Platform"), operated by Iradium Automobiles Private Limited, Pune, Maharashtra, India ("Company", "SparesHub", "we", "us", "our").

By registering as a seller, uploading inventory, listing a lot, accepting a buyer introduction, participating in negotiations, or otherwise using the Platform, the Seller agrees to be legally bound by these Seller Terms. These Seller Terms are in addition to the general Terms of Use and Privacy Policy of CarPartsAuction.in. In the event of conflict regarding seller obligations, these Seller Terms shall prevail.

1. Seller Eligibility and Authority
The Seller represents and warrants that:
* it is legally entitled to sell the inventory offered on the Platform;
* the person registering or signing these Seller Terms has authority to bind the Seller;
* the Seller's business information is accurate;
* the Seller has obtained all internal approvals necessary to list and sell the goods;
* the goods are not stolen, counterfeit, illegally obtained or subject to any undisclosed lien, charge or third-party claim.
The Company may request supporting documents at any stage.

2. Nature of the Platform
CarPartsAuction.in facilitates the listing, discovery, inspection, bidding, negotiation and sale of automobile spare-parts inventory. The Platform may introduce potential buyers to Sellers and facilitate communication, negotiation, inspection, Escrow, payment, logistics or transaction closure. The Company is not obligated to guarantee any minimum sale value, number of buyers, number of bids or successful transaction.

3. Seller Listings
The Seller may upload inventory as one or more lots. The Seller is responsible for ensuring that information provided is materially accurate, including:
* part numbers;
* descriptions;
* quantities;
* brands;
* applicable models;
* MRP;
* stock age;
* condition;
* packaging condition;
* location;
* images;
* remarks;
* supporting documentation.
The Seller must promptly notify the Company of any material discrepancy.

4. Seller Representations Regarding Inventory
The Seller represents that:
* inventory exists and is available for sale;
* quantities stated are reasonably accurate;
* known damage or deterioration has been disclosed;
* packaging condition has been reasonably represented;
* any legal or contractual restriction affecting sale has been disclosed;
* invoices and tax documentation will be provided where legally required.
The Seller shall remain responsible for claims arising from false, misleading or materially incomplete inventory information.

5. Buyer Introductions by SparesHub
A buyer shall be considered an "Introduced Buyer" if the buyer or any related entity, representative, employee, affiliate, partner or agent is:
* introduced to the Seller by SparesHub or CarPartsAuction.in;
* identified to the Seller through the Platform;
* brought to an inspection arranged through the Platform;
* connected through telephone, email, WhatsApp or other communication facilitated by SparesHub;
* disclosed as an interested buyer by SparesHub;
* participating in an EOI, bid, proposal or negotiation relating to the Seller's lot;
* otherwise made known to the Seller as a result of SparesHub's efforts.
An introduction does not require a formal written introduction email to qualify.

6. Non-Circumvention Obligation
The Seller shall not, directly or indirectly, bypass, circumvent or attempt to bypass SparesHub in relation to any Introduced Buyer. Without limiting the above, the Seller shall not:
* sell directly to an Introduced Buyer outside the Platform;
* negotiate directly with an Introduced Buyer in a manner intended to exclude SparesHub;
* accept payment directly from an Introduced Buyer contrary to the transaction structure communicated by SparesHub;
* enter into a side agreement with an Introduced Buyer;
* route the transaction through another company, related party, employee, agent or intermediary;
* create a new invoice, quotation or arrangement outside the Platform to avoid SparesHub;
* sell substantially the same inventory to an Introduced Buyer outside the Platform;
* divide or restructure a transaction for the purpose of avoiding fees or involvement of SparesHub;
* encourage an Introduced Buyer to deal directly with the Seller.
This restriction applies irrespective of who initiates the direct approach.

7. Non-Circumvention Period
The non-circumvention obligation shall apply during the Seller's relationship with SparesHub and for 24 months from the date of the most recent introduction, communication, inspection, EOI, bid or negotiation involving the Introduced Buyer, whichever is later. This applies to transactions involving:
* the originally listed lot;
* part of the original lot;
* substantially similar inventory;
* replacement inventory;
* future spare-parts stock sold to the same Introduced Buyer where the relationship arose from SparesHub's introduction.

8. Existing Buyer Relationships
If the Seller claims that an Introduced Buyer was already an existing customer or known business relationship before the introduction by SparesHub, the Seller must notify SparesHub in writing within 3 business days of the introduction and provide reasonable documentary evidence of the pre-existing commercial relationship. Failure to notify within this period shall result in the buyer being treated as an Introduced Buyer for purposes of these Seller Terms. A mere prior enquiry, exchange of contact details or historical acquaintance without meaningful commercial interaction shall not automatically qualify as a pre-existing customer relationship.

9. Mandatory Disclosure of Direct Contact
If an Introduced Buyer directly contacts the Seller, the Seller must notify SparesHub within 24 hours. The Seller shall not conceal:
* calls;
* meetings;
* visits;
* quotations;
* negotiations;
* purchase orders;
* invoices;
* payments;
* arrangements made with an Introduced Buyer.
The Seller shall reasonably cooperate with SparesHub in documenting and completing the transaction.

10. Penalty for Circumvention
If the Seller directly or indirectly circumvents SparesHub in breach of these Seller Terms, the Seller shall be liable to pay the Company 10% of the gross transaction or lot value, calculated on the higher of:
* the actual transaction value;
* the accepted or negotiated lot value;
* the invoice value;
* the reasonably ascertainable commercial value of the inventory transferred, plus applicable taxes, recovery costs and damages suffered by the Company.
The parties acknowledge that the above amount represents a genuine pre-estimate of the commercial loss, opportunity loss, platform value and transaction facilitation value suffered by SparesHub as a result of circumvention. Payment of such amount shall not prevent the Company from claiming additional damages, injunctive relief or other remedies where actual losses exceed the above amount.

11. Multiple or Concealed Transactions
Where circumvention involves multiple invoices, split transactions, related entities or indirect transfers, all such transactions may be aggregated for calculation of the amount payable under the non-circumvention clause. The Company may rely on:
* invoices;
* transport records;
* GST records where lawfully available;
* buyer confirmations;
* payment evidence;
* communications;
* inspection records;
* purchase orders;
* other commercial records to establish the existence or value of a bypassed transaction.

12. Buyer Confidentiality
Buyer information disclosed to the Seller is confidential. The Seller shall use buyer information solely for the transaction facilitated through CarPartsAuction.in. The Seller must not:
* sell or distribute buyer contact information;
* add buyer details to unrelated marketing databases without lawful basis;
* share buyer data with competitors or third parties;
* solicit buyers outside the approved transaction process;
* use buyer information to build a competing customer database.

13. Confidential Information
"Confidential Information" includes any non-public information disclosed by the Company or an Introduced Buyer, including:
* buyer identity;
* buyer contact details;
* bid values;
* EOI details;
* proposed purchase quantities;
* negotiated prices;
* commercial terms;
* transaction structure;
* Escrow arrangements;
* business strategy;
* customer lists;
* internal processes;
* software or Platform information;
* pricing methodologies;
* communications;
* documents identified as confidential.
The Seller agrees to maintain such information in strict confidence.

14. Non-Disclosure Agreement
The Seller shall:
* use Confidential Information only for evaluating or completing transactions through the Platform;
* disclose it only to employees or advisers who genuinely require access;
* ensure such persons maintain confidentiality;
* protect Confidential Information using reasonable safeguards;
* not copy, reproduce or distribute Confidential Information except where required for the transaction;
* promptly notify SparesHub of unauthorized disclosure or misuse.
The Seller shall not disclose Confidential Information to any competitor, other seller, buyer, broker or third party without prior written consent.

15. NDA Exclusions
Confidential Information does not include information that the Seller can demonstrate:
* was lawfully known before disclosure;
* became publicly available without breach of these Seller Terms;
* was lawfully received from an independent third party;
* was independently developed without use of the Confidential Information.
The burden of demonstrating an exclusion rests with the Seller.

16. Required Disclosure
If the Seller is legally required to disclose Confidential Information, it shall, where legally permitted:
* notify the Company in advance;
* disclose only the minimum information required;
* cooperate in seeking confidentiality protection.

17. Confidentiality Period
Confidentiality obligations shall continue for 5 years after disclosure, except for:
* trade secrets;
* customer databases;
* proprietary processes;
* security information;
which shall remain confidential for so long as they remain confidential in nature. The non-circumvention provisions shall continue for the period specified separately under these Seller Terms.

18. Buyer Inspections
Where inspection is offered, the Seller agrees to provide reasonable access to inventory at the agreed location and time. The Seller shall not use an inspection arranged by SparesHub as an opportunity to bypass the Platform. Any direct commercial discussion during inspection must remain subject to these Seller Terms.

19. Communication With Introduced Buyers
The Company may permit direct interaction where necessary for technical inspection or commercial clarification. Such permission does not waive SparesHub's rights under the non-circumvention provisions. Direct exchange of telephone numbers, emails, business cards or addresses does not authorize a transaction outside SparesHub.

20. Sale Confirmation
The Seller shall not treat a bid or EOI as a completed sale unless final commercial terms have been agreed. Where SparesHub coordinates final negotiation, the Seller shall promptly communicate:
* acceptance;
* rejection;
* counter-offer;
* availability changes.

21. Escrow
Where SparesHub specifies that payment will be made through Escrow, the Seller agrees to comply with the Escrow process applicable to the transaction. The Seller shall not request the buyer to make payment outside the agreed Escrow arrangement without prior written approval from SparesHub. Any attempt to move payment outside the agreed Escrow process may constitute circumvention.

22. Taxes and Invoicing
The Seller is responsible for:
* issuing legally valid invoices;
* charging applicable GST;
* reporting transactions correctly;
* complying with its tax obligations;
* providing documentation required for transaction completion.
The Company is not responsible for the Seller's tax compliance.

23. Logistics and Handover
The Seller shall:
* make goods ready within the agreed timeline;
* provide accurate pickup location;
* cooperate with the buyer or nominated transporter;
* provide reasonable packing appropriate to the agreed transaction;
* confirm handover where required.
Responsibility for freight, loading, insurance and risk transfer shall be determined for each transaction.

24. No Withdrawal After Final Acceptance
Once the Seller has formally accepted a buyer's final offer and the transaction has been confirmed, the Seller shall not withdraw the lot merely because:
* another buyer offers a higher price;
* the Seller changes its commercial preference;
* market prices change.
Any withdrawal after confirmation may result in suspension, recovery of costs and other remedies available to SparesHub.

25. No Misrepresentation
The Seller shall not:
* falsely claim inventory ownership;
* inflate quantities;
* provide misleading MRP;
* conceal damaged stock;
* substitute stock after inspection;
* misrepresent authenticity;
* provide false photographs or documents.
Material misrepresentation may result in immediate suspension and liability for resulting losses.

26. Records and Audit Cooperation
For transactions involving a dispute or suspected circumvention, the Seller shall reasonably cooperate by providing relevant transaction records. Such records may include:
* invoices;
* stock issue records;
* purchase orders;
* dispatch documents;
* payment references;
* correspondence.
The Company shall use such information only for legitimate verification, enforcement or dispute-resolution purposes.

27. Platform Fees and Commercial Charges
Any listing fee, transaction fee, facilitation fee, success fee, Escrow charge or other commercial fee applicable to a transaction may be communicated separately. The Seller agrees to pay applicable charges once accepted. Failure to disclose a fee in the public listing does not invalidate a fee separately agreed in writing.

28. Suspension for Bypass Risk
The Company may immediately suspend or restrict a Seller account where there is reasonable evidence of:
* attempted circumvention;
* concealed buyer contact;
* direct billing to an Introduced Buyer;
* repeated off-platform negotiation;
* misuse of buyer information;
* breach of confidentiality.
Suspension may occur while the matter is investigated.

29. Indemnity
The Seller agrees to indemnify and hold harmless Iradium Automobiles Private Limited, its directors, officers, employees and representatives against losses, claims, damages, penalties, costs and expenses resulting from:
* breach of these Seller Terms;
* circumvention;
* breach of confidentiality;
* inaccurate inventory information;
* counterfeit or unlawful inventory;
* third-party claims relating to ownership;
* tax or regulatory breaches by the Seller;
* direct dealings with an Introduced Buyer contrary to these Terms.

30. Injunctive Relief
The Seller acknowledges that unauthorized use of buyer information, breach of confidentiality or circumvention may cause harm that cannot always be adequately compensated by monetary damages alone. The Company shall therefore be entitled to seek appropriate injunctive or equitable relief in addition to monetary remedies.

31. Survival
The following obligations shall survive account closure, suspension or termination:
* non-circumvention;
* confidentiality;
* NDA obligations;
* payment obligations;
* indemnities;
* dispute provisions;
* intellectual-property obligations.
Closing a Seller account does not terminate obligations relating to buyers introduced before closure.

32. Electronic Acceptance
The Seller agrees that selecting: "I Agree", "Accept Seller Terms", or any equivalent electronic acceptance mechanism shall constitute a valid and binding acceptance of these Seller Terms. Electronic records maintained by the Platform may be relied upon as evidence of acceptance.

33. Amendments
The Company may update these Seller Terms from time to time. Material changes may be communicated through the Platform or registered contact details. Transactions already confirmed may remain subject to the terms applicable at the time of confirmation unless otherwise required.

34. Governing Law and Jurisdiction
These Seller Terms shall be governed by the laws of India. Courts at Pune, Maharashtra shall have exclusive jurisdiction, subject to any arbitration clause separately agreed between the parties.

35. Contact
Iradium Automobiles Private Limited
Pune, Maharashtra, India
Contact Person: Tapas Gupta
Email: contact@spareshub.com
Phone: +91 7875523381
Platform: CarPartsAuction.in`;

export const LEGAL = {
  termsOfUse: { title: "Terms of Use", raw: TERMS_OF_USE },
  privacyPolicy: { title: "Privacy Policy", raw: PRIVACY_POLICY },
  legalDisclaimer: { title: "Legal Disclaimer", raw: LEGAL_DISCLAIMER },
  buyerTerms: { title: "Buyer Terms and Conditions", raw: BUYER_TERMS },
  sellerTerms: { title: "Seller Terms and Conditions", raw: SELLER_TERMS },
};
